import { CountryStateProps } from "@components/CountryStateSection/interfaces";
import formStyles from "@components/Form/FormFields/FormFields.module.scss";
import { getErrorMessage } from "@utilities/getErrorMessage";
import { useAppInsights } from "@utilities/tracking/useAppInsights";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Controller, FormState } from "react-hook-form";
import Arrow from "../../public/assets2019/img/svg/arrow-carousel.svg";
import { getCountries, getCountryStates } from "../../services/forms";
import useOnClickOutside from "../../utils/hooks/useOnClickOutside";
import styles from "./CountryStateSection.module.scss";

const getCountrySelectState = (
  formState: FormState<unknown>,
  inputName: string
) => {
  const hasError = !!formState?.errors?.[inputName];
  const isSubmitted = formState.isSubmitted;
  const isTouched = !!formState?.touchedFields?.[inputName] || isSubmitted;

  return { hasError, isTouched, isSubmitted };
};

const getInputName = (isNationalityVariant: boolean) =>
  isNationalityVariant ? "selectedNationalCountry" : "selectedCountry";

const CountryStateSection = (props: CountryStateProps) => {
  const {
    register,
    isNationalityVariant,
    isShowRelatedStateField,
    countryOfResidenceLabelText,
    countryStateLabelText,
    countryInputValue,
    setCountryInputValue,
    countries,
    setCountries,
    selectedCountry,
    setSelectedCountry,
    showCountriesList,
    setShowCountriesList,
    countriesStateList,
    setCountriesStateList,
    selectedCountryState,
    setSelectedCountryState,
    control,
    setValue,
    countryValidationMsg,
    formState,
  } = props;

  const inputName = getInputName(isNationalityVariant);
  const { hasError, isTouched, isSubmitted } = getCountrySelectState(
    formState,
    inputName
  );

  const [error, setError] = useState(false);
  const [isFocusedOrValue, setIsFocusedOrValue] = useState(false);

  const fieldRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(fieldRef, () => handleOnBlur());

  const hasCountryMatch = (value: string) =>
    countries && countries.find((country) => country === value);

  const appInsights = useAppInsights();
  const logToAppInsight = (error: string) => {
    appInsights?.trackEvent({
      name: "Request to Fetch Countries Failed",
      properties: { error },
    });
  };

  useEffect(() => {
    const fetchCountryData = async () => {
      try {
        const value = (countryInputValue || "").trim();
        if (value.length > 0) {
          const countriesResponse = await getCountries(value);
          setCountries(countriesResponse);
          setError(countriesResponse.length === 0);
        } else {
          setCountries([]);
        }
      } catch (error) {
        logToAppInsight(getErrorMessage(error));
        setCountries([]);
      }
    };
    if (selectedCountry !== countryInputValue?.trim()) {
      fetchCountryData();
    }
    if (countryInputValue) {
      setIsFocusedOrValue(true);
    }
  }, [countryInputValue]);

  useEffect(() => {
    const value = (selectedCountry || "").trim();
    if (!value?.length) return;
    const fetchCountryStateData = async () => {
      try {
        const selectedCountryState = await getCountryStates(value);
        setCountriesStateList?.(selectedCountryState);
      } catch (error) {
        logToAppInsight(getErrorMessage(error));
        setCountriesStateList?.([]);
      }
    };

    if (isShowRelatedStateField) {
      fetchCountryStateData();
    } else {
      setCountriesStateList?.([]);
    }

    setCountryInputValue(selectedCountry);
    setValue(inputName, selectedCountry);
  }, [selectedCountry]);

  useEffect(() => {
    if (countries) {
      setShowCountriesList(true);
    } else if (countryInputValue === selectedCountry || !countries) {
      setShowCountriesList(false);
    }
  }, [countries]);

  const handleCountriesChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCountryInputValue(event.target.value);
    if (hasCountryMatch(event.target.value)) {
      setError(false);
    } else {
      setValue(inputName, null);
      setShowCountriesList(true);
    }

    if (!countries) {
      setShowCountriesList(false);
    }
  };

  const handleCountryOptionSelect = (country: string) => {
    setCountryInputValue(country);
    setSelectedCountry(country);
    setValue(inputName, country);
    setError(false);
    setShowCountriesList(false);
  };

  const handleOnBlur = () => {
    if (countries?.length && !hasCountryMatch(countryInputValue)) {
      setError(true);
    }
    if (isSubmitted && !countryInputValue) {
      setError(true);
    } else {
      setSelectedCountry(countryInputValue);
    }

    setShowCountriesList(false);
  };

  const handleOnFocus = () => {
    if (!countryInputValue) {
      setIsFocusedOrValue(true);
      setShowCountriesList(false);
      return;
    }
    setIsFocusedOrValue(true);
    setShowCountriesList(false);
  };

  const handleCountryStateChange = (e) => {
    setSelectedCountryState?.(e.target.value);
  };

  const getCountryValidation = () => {
    if (countryValidationMsg) return countryValidationMsg;
    return !isNationalityVariant
      ? "Country of residence is required"
      : "Country of nationality is required";
  };

  const isShowError = isTouched && (error || (hasError && !countryInputValue));
  const describedby = isShowError ? `${inputName}-message` : undefined;

  return (
    <div
      className={clsx(
        "form",
        "form-row",
        styles["form-row"],
        formStyles["form-row"],
        styles["country-section"],
        isFocusedOrValue && formStyles.focused,
        isShowError && formStyles.error
      )}
    >
      <div className={formStyles["form-label"]}>
        <div className={formStyles.label}>
          <label htmlFor={inputName}>
            {countryOfResidenceLabelText}
          </label>
        </div>
      </div>
      <div className={formStyles["form-field"]}>
        <div className={formStyles.field} ref={fieldRef}>
          <Controller
            name={inputName}
            control={control}
            rules={{ required: "This field is required" }}
            render={({ field }) => (
              <>
                <input
                  {...field}
                  className={clsx("smart-focus", styles["homecountry-box"])}
                  type="text"
                  value={countryInputValue}
                  autoComplete="off"
                  onChange={handleCountriesChange}
                  onFocus={handleOnFocus}
                  id={inputName}
                  aria-invalid={isShowError}
                  aria-describedby={describedby}
                  aria-required
                />
                {showCountriesList && countries?.length > 0 && (
                  <ul className={styles["countries-wrapper"]}>
                    {countries?.map?.((country, index) => (
                      <li
                        key={`${country}${index}`}
                        className={styles["countries-item"]}
                        value={country}
                        role="option"
                        aria-selected={
                          country.toLowerCase() ===
                          countryInputValue.toLowerCase()
                        }
                        tabIndex={0}
                        {...register(getCountryValidation())}
                        onClick={() => handleCountryOptionSelect(country)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleCountryOptionSelect(country);
                          }
                        }}
                      >
                        {country}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          />
        </div>

        {isShowError && (
          <div className="form-row error">
            <div className="form-field base6">
              <div
                className={clsx(
                  formStyles["message-wrapper"],
                  "field",
                  "message-wrapper",
                  "inline-message",
                  "field_error_msg"
                )}
                id={`${inputName}-message`}
              >
                <p className={clsx(formStyles.message, formStyles.active)}>
                  <span className="sr-only">Error: </span>
                  {!countryInputValue || countryInputValue?.length <= 0
                    ? getCountryValidation()
                    : "Unknown country entered"}
                </p>
                {isNationalityVariant}
              </div>
            </div>
          </div>
        )}
      </div>

      {!error &&
        countryInputValue?.length > 0 &&
        countriesStateList?.length > 0 && (
          <div id="homestatediv" className={styles["country-state_wrapper"]}>
            <div
              className={clsx(
                "form-row field-row select-wrapper",
                formStyles["form-row"],
                formStyles["field-row"],
                formStyles["select-wrapper"]
              )}
            >
              <div
                className={clsx("form-label base4", formStyles["form-label"])}
              >
                <div className="label">
                  <label htmlFor="CountryState">
                    {countryStateLabelText}
                  </label>
                </div>
              </div>
              <div
                className={clsx("form-field base6", formStyles["form-field"])}
              >
                <div
                  className={clsx(
                    "field select",
                    formStyles["field"],
                    formStyles["select"]
                  )}
                >
                  {
                    <>
                      <select
                        className={clsx(
                          "homestate-list input-validation-error"
                        )}
                        id="CountryState"
                        name="CountryState"
                        aria-invalid="false"
                        autoComplete="off"
                        value={selectedCountryState}
                        {...register(
                          `${
                            !isNationalityVariant
                              ? "selectedState"
                              : "selectedNationalState"
                          }`
                        )}
                        onChange={handleCountryStateChange}
                      >
                        <option value="">Select</option>
                        {countriesStateList?.map?.((option, index) => (
                          <option value={option} key={`${option}${index}`}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </>
                  }
                  <div
                    className={clsx("tick icon-chevron", formStyles["tick"])}
                  >
                    <i className="icon-chevron">
                      <Arrow></Arrow>
                    </i>
                  </div>
                </div>

                {error && (
                  <div
                    className={clsx(
                      "form-row error",
                      formStyles["form-row"],
                      formStyles["error"]
                    )}
                  >
                    <div className="form-field base6">
                      <div
                        className={clsx(
                          "field",
                          "message-wrapper",
                          "inline-message",
                          "field_error_msg",
                          formStyles["field"],
                          formStyles["message-wrapper"]
                        )}
                      >
                        <p
                          className={clsx(
                            "message",
                            "active",
                            formStyles["message"]
                          )}
                        >
                          You must select a County/State
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default CountryStateSection;
